import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { binencode } from "@/lib/binary";
import {
  MessageBodyType,
  MessageDestinationType,
  MessagePacketType,
  WiredMessage,
  WiredMessageBody,
  WiredMessagePacket,
} from "@/moby/db";
import { mui } from "@/mui";
import * as Listbox from "@diceui/listbox";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { CheckIcon, MailIcon, SendIcon, AlertCircle, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { type ReactNode, useState, useEffect } from "react";
import { toast } from "sonner";
import type bin from "typed-binary";
import { useLocation } from "wouter";
import {
  createAndSignGroupInvite,
  createGroupInviteMessage,
  useMobyDB,
  useMobyNodeClient,
  useSession,
} from "../provider";
import { createAndSignContactDetails } from "@/moby/repo";

interface EmailAccount {
  id: string;
  email: string;
  enabled: boolean;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  authType: "password" | "oauth2";
  accessToken?: string;
}

function getEmailGatewayUrl(): string {
  try {
    const stored = localStorage.getItem('moby_pref_emailGatewayUrl');
    if (stored) return stored;
    const legacy = localStorage.getItem('localPreferences');
    if (legacy) return JSON.parse(legacy).emailGatewayUrl || '';
  } catch { /* ignore */ }
  return '';
}

function getEmailAccounts(): EmailAccount[] {
  try {
    const stored = localStorage.getItem("moby_pref_emailAccounts");
    if (stored) return JSON.parse(stored) as EmailAccount[];
  } catch { /* ignore */ }
  return [];
}

export function NewGroupChatDialog({ children, open: controlledOpen, onOpenChange }: { children: ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  const db = useMobyDB();
  const node = useMobyNodeClient();
  const session = useSession();
  if (session === undefined) {
    throw new Error("Session is undefined");
  }
  const navigate = useLocation()[1];
  const [internalOpen, setInternalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedContactAddresses, setSelectedContactAddresses] = useState<
    string[]
  >([]);
  
  // Email invite state
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [selectedFromAccount, setSelectedFromAccount] = useState<string>('');
  const [showEmailInvite, setShowEmailInvite] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  
  const emailAccounts = getEmailAccounts().filter(a => a.enabled);
  const contactDetails = createAndSignContactDetails(session);
  const shareCode = contactDetails ? bytesToHex(contactDetails) : '';
  
  useEffect(() => {
    if (!selectedFromAccount && emailAccounts.length > 0) {
      setSelectedFromAccount(emailAccounts[0].id);
    }
  }, [emailAccounts.length, selectedFromAccount]);
  
  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const contacts = useSuspenseLiveQuery(async () => {
    const contacts = await db.contacts.toArray();
    return contacts;
  }, []);

  const onSubmit = async () => {
    const secret = ed25519.utils.randomPrivateKey();
    const address = ed25519.getPublicKey(secret);

    const first: bin.Parsed<typeof WiredMessageBody> = {
      type: MessageBodyType.GROUP_JOIN,
      groupAddress: address,
      groupInvite: createAndSignGroupInvite(bytesToHex(address), session),
      from: {
        address: hexToBytes(session.address),
        nonce: 1,
      },
      version: 1,
      timestamp: BigInt(Date.now()),
    };

    const firstMessage = {
      signature: ed25519.sign(
        binencode(WiredMessageBody, first),
        hexToBytes(session.secret),
      ),
      body: first,
    } satisfies bin.Parsed<typeof WiredMessage>;

    const inviteMessagePromises: ReturnType<typeof createGroupInviteMessage>[] =
      [];

    for (const inviteeAddress of selectedContactAddresses) {
      inviteMessagePromises.push(
        createGroupInviteMessage(
          db,
          bytesToHex(address),
          session,
          inviteeAddress,
        ),
      );
    }

    const inviteMessages = await Promise.all(inviteMessagePromises);

    await db.transaction("rw", db.chats, db.messages, async () => {
      await db.chats.add({
        address: bytesToHex(address),
        secret: bytesToHex(secret),
        type: MessageDestinationType.GROUP,
        name: groupName,
        peers: [],
        nonces: {},
      });

      await db.storeGroupJoinMessage(session.address, firstMessage);

      await Promise.all(
        inviteMessages.map((inviteMessage) =>
          db.storeMessage(session.address, inviteMessage),
        ),
      );
    });

    for (const inviteMessage of inviteMessages) {
      const peer = node.getOrCreatePeer(
        bytesToHex(inviteMessage.body.to.address),
      );

      if (peer.connection.connectionState !== "connected") {
        continue;
      }

      void peer.chat.send(
        binencode(WiredMessagePacket, {
          type: MessagePacketType.MESSAGE,
          message: inviteMessage,
        }),
      );
    }

    // Send email invites for non-contact recipients
    const emailAddresses = inviteEmails
      .split(/[,;]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    if (emailAddresses.length > 0) {
      const gatewayUrl = getEmailGatewayUrl();
      const account = emailAccounts.find(a => a.id === selectedFromAccount);
      const groupAddressHex = bytesToHex(address);
      
      if (gatewayUrl && account) {
        const credentials: { user: string; pass?: string; accessToken?: string } = {
          user: account.username,
        };
        if (account.authType === "oauth2" && account.accessToken) {
          credentials.accessToken = account.accessToken;
        } else {
          credentials.pass = account.password;
        }
        
        const inviteCode = '9MOBYCHAT2025INVITE999';
        const inviteLink = `${window.location.origin}/chats/?invite=${inviteCode}#${shareCode}`;
        const personalNote = inviteMessage.trim() ? `<p>${inviteMessage.trim()}</p>` : '';
        
        const emailBody = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
${personalNote}
<p>You've been invited to join the group <strong>${groupName}</strong> on MobyChat!</p>

<p><strong>New to MobyChat?</strong><br>
Use this Invite Code during signup: <strong style="color: #0066cc;">${inviteCode}</strong></p>

<p><a href="${inviteLink}" style="display: inline-block; padding: 12px 24px; background-color: #0066cc; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">Click Here to Connect</a></p>

<p style="margin-top: 24px; color: #666;">Or manually paste this code in MobyChat:</p>
<p style="font-family: monospace; background-color: #f5f5f5; padding: 12px; border-radius: 4px; word-break: break-all;">
${shareCode}
</p>

<hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">
<p style="color: #888; font-size: 12px;">MobyChat - Secure, decentralized messaging</p>
</body>
</html>`;
        
        for (const recipientEmail of emailAddresses) {
          try {
            const response = await fetch(`${gatewayUrl}/api/email/smtp/send`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                host: account.smtpHost,
                port: account.smtpPort,
                secure: account.smtpPort === 465,
                credentials,
                from: account.email,
                to: recipientEmail,
                subject: `${session?.name || 'Someone'} invited you to ${groupName} on MobyChat`,
                body: emailBody,
                html: true,
              }),
            });
            
            const data = await response.json().catch(() => ({ success: false }));
            
            if (response.ok && data.success) {
              await db.pendingInvites.add({
                id: crypto.randomUUID(),
                recipientEmail,
                senderAddress: session?.address || '',
                senderKeyString: shareCode,
                status: 'pending',
                createdAt: new Date(),
                expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                personalMessage: inviteMessage.trim() || undefined,
                groupAddress: groupAddressHex,
                groupName,
                syncedToServer: 0,
              });
              console.log('[GROUP_INVITE] Email invite sent', { recipientEmail, groupName });
            } else {
              console.warn('[GROUP_INVITE] Email failed for', recipientEmail);
            }
          } catch (err) {
            console.error('[GROUP_INVITE] Email send error:', err);
          }
        }
      }
    }

    toast.success("Group successfully created!");

    navigate(`/chats/${bytesToHex(address)}`);

    setOpen(false);
  };

  return (
    <mui.Dialog.Root
      open={open}
      onOpenChange={(open) => {
        setOpen(open);

        if (!open) {
          setGroupName("");
          setSelectedContactAddresses([]);
          setInviteEmails('');
          setInviteMessage('');
          setShowEmailInvite(false);
        }
      }}
    >
      {children}
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop onClick={(e) => e.stopPropagation()} />
        <mui.Dialog.Popup onClick={(e) => e.stopPropagation()}>
          <mui.Dialog.Title>New Group Chat</mui.Dialog.Title>
          <mui.Dialog.Description>
            Select contacts or invite new people via email.
          </mui.Dialog.Description>

          <div className="relative mt-6">
            <mui.Input
              type="text"
              id="group-name"
              name="group-name"
              filled
              variant="surface"
              className="truncate"
              autoFocus
              autoComplete="off"
              maxLength={32}
              placeholder=""
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <mui.FloatingLabel variant="surface" htmlFor="group-name">
              Group name
            </mui.FloatingLabel>

            <mui.InputError />
          </div>

          <Listbox.Root
            className="border-t-outline -mx-6 mt-4 max-h-64 overflow-x-hidden overflow-y-auto border-t"
            multiple
            value={selectedContactAddresses}
            onValueChange={setSelectedContactAddresses}
          >
            {contacts.map((row) => {
              return (
                <Listbox.Item
                  key={row.address}
                  value={row.address}
                  className="border-b-outline group relative block border-b p-4 hover:cursor-pointer"
                >
                  <span
                    aria-hidden
                    className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 transition"
                  />

                  <div className="flex gap-4">
                    <div className="relative size-9 shrink-0">
                      <div 
                        className="grid size-9 place-items-center rounded-full text-white"
                        style={{ backgroundColor: getPeerAvatarColor(row.custom?.profile) }}
                      >
                        <div className="text-label-md">{row.getInitials()}</div>
                        {renderPeerAvatarEmoji(row.custom?.profile, "sm")}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-title-md truncate">
                        {row.getDisplayName()}
                      </div>
                      <div className="text-label-sm truncate font-mono">
                        {row.address}
                      </div>
                    </div>
                    <Listbox.ItemIndicator className="self-center">
                      <CheckIcon size={16} />
                    </Listbox.ItemIndicator>
                  </div>
                </Listbox.Item>
              );
            })}
          </Listbox.Root>

          {/* Email Invite Section - Collapsible */}
          <div className="-mx-6 border-t border-outline-variant">
            <button
              type="button"
              onClick={() => setShowEmailInvite(!showEmailInvite)}
              className="w-full px-6 py-3 flex items-center justify-between hover:bg-on-surface-variant/5 transition"
            >
              <div className="flex items-center gap-2 text-body-md">
                <MailIcon size={16} className="text-primary" />
                <span>Invite via Email</span>
              </div>
              {showEmailInvite ? (
                <ChevronUpIcon size={16} className="text-on-surface-variant" />
              ) : (
                <ChevronDownIcon size={16} className="text-on-surface-variant" />
              )}
            </button>
            
            {showEmailInvite && (
              <div className="px-6 pb-4 space-y-3">
                <input
                  type="text"
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
                />
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Personal message (optional)"
                  rows={2}
                  className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary resize-none"
                />
                <div className="flex items-center gap-2">
                  <span className="text-label-sm text-on-surface-variant whitespace-nowrap">From:</span>
                  {emailAccounts.length > 0 ? (
                    <select
                      value={selectedFromAccount}
                      onChange={(e) => setSelectedFromAccount(e.target.value)}
                      aria-label="Select sender email account"
                      className="flex-1 px-2 py-1.5 bg-surface border border-outline-variant rounded-lg text-body-sm text-on-surface focus:outline-none focus:border-primary"
                    >
                      {emailAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.email}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex-1 flex items-center gap-2 text-body-sm text-on-surface-variant">
                      <AlertCircle size={14} className="text-warning" />
                      <span>Add email account in Settings</span>
                    </div>
                  )}
                </div>
                <div className="text-label-sm text-on-surface-variant">
                  Email invitees will receive your contact code. Add them to the group after they connect.
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <mui.Button
              variant="primary"
              filled
              className="w-full"
              disabled={
                (selectedContactAddresses.length === 0 && inviteEmails.trim().length === 0) || groupName.length === 0
              }
              onClick={onSubmit}
            >
              Create Group
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}
